// src/pages/Settings.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import GlassCard from '../components/UI/GlassCard';
import { Settings as SettingsIcon, Building2, Receipt, ShieldCheck, Mail, Save, Clock, Percent, Globe, Bell, Smartphone, Palette, AlertCircle, Upload, Database, Calendar, Send, Download, Zap, Monitor, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
    const [ref, setRef] = useState(null);
    return (
        <>
            <Tippy {...props} reference={ref} />
            {React.cloneElement(React.Children.only(children), { ref: setRef })}
        </>
    );
};

const Settings = () => {
    const { can } = useAuth();
    const [activeTab, setActiveTab] = useState('general');
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchSettings = async () => {
        try {
            const response = await axios.get('/backend/api/configuracion/index.php');
            if (response.data.success) {
                setSettings(response.data.data);
            }
        } catch (error) {
            toast.error('Error al cargar ajustes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSettings(); }, []);

    const handleChange = (key, value) => {
        setSettings({ ...settings, [key]: value });
    };

    const [subiendoLogo, setSubiendoLogo] = useState(false);
    const [testSmtpLoading, setTestSmtpLoading] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testSmsLoading, setTestSmsLoading] = useState(false);
    const [descargandoBackup, setDescargandoBackup] = useState(false);

    const handleSave = async () => {
        try {
            const response = await axios.post('/backend/api/configuracion/index.php', settings);
            if (response.data.success) {
                Swal.fire({
                    title: '¡Ajustes Guardados!',
                    text: 'La configuración global del sistema ha sido actualizada.',
                    icon: 'success',
                    background: 'rgba(15, 23, 42, 0.95)',
                    color: '#fff',
                    confirmButtonColor: '#8b5cf6'
                });
            }
        } catch (error) {
            toast.error('Error al guardar cambios');
        }
    };

    const handleTestSMTP = async () => {
        if (!testEmail) {
            toast.error('Ingrese un email para la prueba');
            return;
        }
        setTestSmtpLoading(true);
        try {
            const response = await axios.post('/backend/api/configuracion/test_smtp.php', {
                ...settings,
                test_email: testEmail
            });
            if (response.data.success) {
                Swal.fire({
                    title: 'SMTP Conectado 📧',
                    text: 'El sistema ha enviado exitosamente un correo electrónico de prueba.',
                    icon: 'success',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: '#fff'
                });
            } else {
                Swal.fire({
                    title: 'Error de Conexión',
                    text: response.data.message || 'No se pudo conectar con el servidor SMTP',
                    icon: 'error',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: '#fff'
                });
            }
        } catch (error) {
            toast.error('Falla crítica en el motor de correo');
        } finally {
            setTestSmtpLoading(false);
        }
    };

    const handleUploadLogo = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        setSubiendoLogo(true);
        try {
            const response = await axios.post('/backend/api/configuracion/upload_logo.php', formData);
            if (response.data.success) {
                handleChange('logo_url', response.data.data.url);
                toast.success('Logo actualizado exitosamente');
            }
        } catch (error) {
            toast.error('Error al subir el logo');
        } finally {
            setSubiendoLogo(false);
        }
    };

    const handleTestSMS = async () => {
        setTestSmsLoading(true);
        try {
            const response = await axios.post('/backend/api/configuracion/test_sms.php', settings);
            if (response.data.success) {
                Swal.fire({
                    title: 'Twilio Conectado 📱',
                    text: 'Se ha enviado un SMS de prueba exitosamente.',
                    icon: 'success',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: '#fff'
                });
            } else {
                Swal.fire({
                    title: 'Error Twilio',
                    text: response.data.message || 'Verifique sus credenciales de Twilio',
                    icon: 'error',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: '#fff'
                });
            }
        } catch (error) {
            toast.error('Falla en el motor de SMS');
        } finally {
            setTestSmsLoading(false);
        }
    };

    const handleDownloadBackup = async () => {
        setDescargandoBackup(true);
        try {
            const response = await axios.get('/backend/api/configuracion/backup.php', {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Usar fecha local para el nombre del archivo
            const now = new Date();
            const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().split('T')[0];
            link.setAttribute('download', `pawspa_backup_${localDate}.sql`);
            document.body.appendChild(link);
            link.click();
            toast.success('Copia de seguridad descargada');
        } catch (error) {
            toast.error('Error al generar backup');
        } finally {
            setDescargandoBackup(false);
        }
    };

    const tabs = [
        { id: 'general', label: 'Empresa', icon: Building2 },
        { id: 'fiscal', label: 'Moneda & IVU', icon: Receipt },
        { id: 'notif', label: 'Notificaciones', icon: Bell },
        { id: 'smtp', label: 'Email (SMTP)', icon: Mail },
        { id: 'twilio', label: 'Twilio (SMS)', icon: Smartphone },
        { id: 'security', label: 'Seguridad', icon: ShieldCheck },
        { id: 'agenda_config', label: 'Citas / Agenda', icon: Calendar },
        { id: 'ui', label: 'Apariencia', icon: Palette },
        { id: 'experience', label: 'Experiencia', icon: Zap },
        { id: 'backup', label: 'Respaldo DB', icon: Database }
    ];

    if (loading) return (
        <div className="h-screen w-full flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-extrabold flex items-center gap-4 text-white">
                        <SettingsIcon className="text-primary-light animate-spin-slow" size={42} /> 
                        Panel de <span className="text-primary-light italic font-medium">Control</span>
                    </h1>
                    <p className="text-white/50 mt-2 text-sm lg:text-base font-medium flex items-center gap-2">
                        <ShieldCheck size={14} className="text-emerald-400" />
                        Gobernanza global de PawSpa Pro y parámetros del sistema
                    </p>
                </div>
                
                {can('Configuración', 'editar') && (
                    <SafeTippy content="Salvar cambios en la nube institucional" theme="glass-premium" animation="scale">
                        <button 
                          onClick={handleSave}
                          className="bg-emerald-500 hover:bg-emerald-600 px-10 py-4 rounded-2xl font-black text-white shadow-xl shadow-emerald-500/20 hover:scale-105 transition-all flex items-center gap-3"
                        >
                            <Save size={20} />
                            GUARDAR CONFIGURACIÓN
                        </button>
                    </SafeTippy>
                )}
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sidebar de Ajustes */}
                <div className="space-y-3">
                    {tabs.map(t => (
                        <button 
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            className={`w-full flex items-center gap-4 p-5 rounded-2xl border transition-all ${activeTab === t.id ? 'bg-primary/20 border-primary shadow-lg shadow-primary/10 text-white translate-x-2' : 'glass border-white/5 text-white/40 hover:text-white hover:bg-white/5'}`}
                        >
                            <t.icon size={20} className={activeTab === t.id ? 'text-primary-light' : 'text-white/20'} />
                            <span className="font-bold tracking-tight">{t.label}</span>
                        </button>
                    ))}
                </div>

                {/* Contenido Dinámico */}
                <div className="lg:col-span-3">
                    <GlassCard padding="40px" className="min-h-[500px] border-white/10 shadow-2xl bg-slate-900/40">
                        {activeTab === 'general' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Identidad Corporativa</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Nombre del Negocio</label>
                                        <input type="text" value={settings.nombre_negocio || ''} onChange={e => handleChange('nombre_negocio', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Teléfono Oficial</label>
                                        <input type="text" value={settings.telefono_negocio || ''} onChange={e => handleChange('telefono_negocio', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Dirección Principal</label>
                                        <input type="text" value={settings.direccion_negocio || ''} onChange={e => handleChange('direccion_negocio', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="md:col-span-1 space-y-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2"><Upload size={10}/> Logo de Empresa (Para Facturas y Reportes)</label>
                                        <div className="glass border-white/10 rounded-3xl p-6 flex items-center gap-6 group relative overflow-hidden">
                                            <div className="w-24 h-24 rounded-2xl bg-slate-800/80 border border-white/5 flex items-center justify-center overflow-hidden">
                                                {settings.logo_url ? (
                                                    <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain p-2" />
                                                ) : (
                                                    <Building2 size={32} className="text-white/10" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-[10px] text-white/30 mb-3">Recomendado: PNG Transparente 512x512px</p>
                                                <input type="file" id="logoUpload" className="hidden" accept="image/*" onChange={handleUploadLogo} />
                                                <label htmlFor="logoUpload" className="inline-flex items-center gap-2 bg-white/5 hover:bg-primary px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer">
                                                    {subiendoLogo ? 'SUBIENDO...' : 'SUBIR DESDE PC'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2"><Globe size={10}/> Zona Horaria (Regla #2.4)</label>
                                        <select value={settings.timezone || ''} onChange={e => handleChange('timezone', e.target.value)} className="input-field bg-slate-800/80">
                                            <option value="America/Puerto_Rico">America/Puerto_Rico</option>
                                            <option value="America/New_York">America/New_York</option>
                                            <option value="Europe/Madrid">Europe/Madrid</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2"><Calendar size={10}/> Formato de Fecha</label>
                                        <select value={settings.date_format || 'm/d/Y'} onChange={e => handleChange('date_format', e.target.value)} className="input-field bg-slate-800/80">
                                            <option value="m/d/Y">Mes / Día / Año (m/d/Y)</option>
                                            <option value="d/m/Y">Día / Mes / Año (d/m/Y)</option>
                                            <option value="Y-m-d">Año-Mes-Día (Y-m-d)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'fiscal' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Política de Impuestos y Moneda</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Moneda ISO</label>
                                        <input type="text" value={settings.moneda || ''} onChange={e => handleChange('moneda', e.target.value)} className="input-field bg-slate-800/80" placeholder="USD" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Símbolo</label>
                                        <input type="text" value={settings.simbolo_moneda || ''} onChange={e => handleChange('simbolo_moneda', e.target.value)} className="input-field bg-slate-800/80" placeholder="$" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Nombre Impuesto 1</label>
                                        <input type="text" value={settings.tax_label_1 || 'IVU Estatal'} onChange={e => handleChange('tax_label_1', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-2"><Percent size={10}/> Tasa {settings.tax_label_1 || 'Impuesto 1'}</label>
                                        <input type="number" step="0.001" value={settings.ivu_rate || ''} onChange={e => handleChange('ivu_rate', e.target.value)} className="input-field bg-slate-800/80 font-black text-emerald-400" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Nombre Impuesto 2</label>
                                        <input type="text" value={settings.tax_label_2 || 'IVU Municipal'} onChange={e => handleChange('tax_label_2', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-2"><Percent size={10}/> Tasa {settings.tax_label_2 || 'Impuesto 2'}</label>
                                        <input type="number" step="0.001" value={settings.ivu_municipal || ''} onChange={e => handleChange('ivu_municipal', e.target.value)} className="input-field bg-slate-800/80 font-black text-emerald-400" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'notif' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Canales de Comunicación</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between p-6 rounded-3xl glass border-white/5">
                                        <div className="flex items-center gap-4">
                                            <div className="bg-primary/20 p-3 rounded-2xl text-primary-light"><Mail size={24}/></div>
                                            <div>
                                                <h4 className="font-bold text-white">Notificaciones por Email</h4>
                                                <p className="text-xs text-white/30">Enviar confirmaciones automáticas vía SMTP</p>
                                            </div>
                                        </div>
                                        <input type="checkbox" checked={settings.smtp_enabled === '1'} onChange={e => handleChange('smtp_enabled', e.target.checked ? '1' : '0')} className="w-12 h-6 rounded-full appearance-none bg-white/10 checked:bg-emerald-500 relative transition-colors cursor-pointer before:content-[''] before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-1 before:left-1 checked:before:left-7 before:transition-all" />
                                    </div>
                                    <div className="flex items-center justify-between p-6 rounded-3xl glass border-white/5">
                                        <div className="flex items-center gap-4">
                                            <div className="bg-secondary/20 p-3 rounded-2xl text-secondary"><Smartphone size={24}/></div>
                                            <div>
                                                <h4 className="font-bold text-white">Recordatorios SMS (Regla #2.4)</h4>
                                                <p className="text-xs text-white/30">Integración con Twilio para avisos móviles</p>
                                            </div>
                                        </div>
                                        <input type="checkbox" checked={settings.twilio_enabled === '1'} onChange={e => handleChange('twilio_enabled', e.target.checked ? '1' : '0')} className="w-12 h-6 rounded-full appearance-none bg-white/10 checked:bg-emerald-500 relative transition-colors cursor-pointer before:content-[''] before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-1 before:left-1 checked:before:left-7 before:transition-all" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'agenda_config' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white pb-10">
                                <div className="flex justify-between items-end border-b border-white/5 pb-6">
                                    <div className="space-y-2">
                                        <h3 className="text-2xl font-black text-white flex items-center gap-3">
                                            <Calendar className="text-primary-light" />
                                            Horario Operativo Detallado
                                        </h3>
                                        <p className="text-xs text-white/30 italic">Define la disponibilidad de tu negocio para cada día de la semana.</p>
                                    </div>
                                    <div className="bg-primary/10 px-4 py-2 rounded-2xl border border-primary/20">
                                        <span className="text-[10px] font-black uppercase text-primary-light tracking-widest">Estándar Operativo</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((dayKey) => {
                                        const dayData = settings.business_hours_json ? (typeof settings.business_hours_json === 'string' ? JSON.parse(settings.business_hours_json) : settings.business_hours_json)[dayKey] : {open: '08:00', close: '20:00', active: true};
                                        const dayNames = { mon: 'Lunes', tue: 'Martes', wed: 'Miércoles', thu: 'Jueves', fri: 'Viernes', sat: 'Sábado', sun: 'Domingo' };
                                        
                                        const handleDayChange = (subKey, value) => {
                                            const currentJson = settings.business_hours_json ? (typeof settings.business_hours_json === 'string' ? JSON.parse(settings.business_hours_json) : settings.business_hours_json) : {};
                                            const updatedJson = {
                                                ...currentJson,
                                                [dayKey]: { ...dayData, [subKey]: value }
                                            };
                                            handleChange('business_hours_json', JSON.stringify(updatedJson));
                                        };

                                        return (
                                            <div key={dayKey} className={`flex flex-col md:flex-row items-center justify-between p-6 rounded-3xl border transition-all ${dayData.active ? 'glass border-white/10 bg-white/5' : 'bg-red-500/5 border-red-500/10 opacity-60'}`}>
                                                <div className="flex items-center gap-6 w-full md:w-64">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-inner ${dayData.active ? 'bg-primary/20 text-primary-light' : 'bg-slate-800 text-white/20'}`}>
                                                        {dayKey === 'sun' ? '🏖️' : '✂️'}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-white uppercase tracking-tight">{dayNames[dayKey]}</h4>
                                                        <p className={`text-[10px] font-bold uppercase tracking-widest ${dayData.active ? 'text-emerald-400' : 'text-red-400'}`}>
                                                            {dayData.active ? 'Abierto' : 'Cerrado'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex-1 flex flex-col md:flex-row items-center gap-8 w-full mt-6 md:mt-0">
                                                    <div className="flex items-center gap-4 flex-1 w-full md:w-auto">
                                                        <div className="flex-1 space-y-2">
                                                            <label className="text-[9px] font-black uppercase text-white/30 tracking-widest ml-1">Apertura</label>
                                                            <input 
                                                                type="time" 
                                                                disabled={!dayData.active}
                                                                value={dayData.open} 
                                                                onChange={(e) => handleDayChange('open', e.target.value)}
                                                                className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-4 py-3 text-sm font-black text-white [color-scheme:dark] outline-none focus:border-primary transition-all disabled:opacity-20"
                                                            />
                                                        </div>
                                                        <div className="pt-6 text-white/10">
                                                            <Zap size={14} />
                                                        </div>
                                                        <div className="flex-1 space-y-2">
                                                            <label className="text-[9px] font-black uppercase text-white/30 tracking-widest ml-1">Cierre</label>
                                                            <input 
                                                                type="time" 
                                                                disabled={!dayData.active}
                                                                value={dayData.close} 
                                                                onChange={(e) => handleDayChange('close', e.target.value)}
                                                                className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-4 py-3 text-sm font-black text-white [color-scheme:dark] outline-none focus:border-primary transition-all disabled:opacity-20"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-4 md:border-l md:border-white/5 md:pl-8">
                                                        <button 
                                                            onClick={() => handleDayChange('active', !dayData.active)}
                                                            className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-tighter transition-all ${dayData.active ? 'bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white' : 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'}`}
                                                        >
                                                            {dayData.active ? 'CERRAR DÍA' : 'ABRIR DÍA'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="p-8 rounded-3xl bg-amber-500/10 border border-amber-500/20 flex gap-6 items-center">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-500 flex-shrink-0">
                                        <AlertCircle size={24} />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm font-black text-white uppercase tracking-tight">Sincronización de Agenda</p>
                                        <p className="text-xs text-white/50 leading-relaxed font-medium">
                                            Los cambios realizados aquí se reflejarán instantáneamente en la cuadrícula de la agenda. Los días marcados como "Cerrado" no permitirán arrastrar servicios ni crear citas nuevas.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'security' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Seguridad y Acceso</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2"><Clock size={10}/> Auto Logout (Minutos)</label>
                                        <input type="number" value={settings.auto_logout_minutos || ''} onChange={e => handleChange('auto_logout_minutos', e.target.value)} className="input-field bg-slate-800/80 font-bold" />
                                        <p className="text-[10px] text-white/20 italic">El usuario será desconectado tras este periodo de inactividad.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'ui' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Apariencia del Sistema</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2"><Palette size={10}/> Color Primario</label>
                                        <div className="flex items-center gap-4">
                                            <input type="color" value={settings.color_primario || '#8b5cf6'} onChange={e => handleChange('color_primario', e.target.value)} className="w-16 h-16 rounded-2xl bg-white/5 border-none cursor-pointer" />
                                            <div>
                                                <p className="text-sm font-bold text-white uppercase">{settings.color_primario || '#8b5cf6'}</p>
                                                <p className="text-[10px] text-white/30">Identidad de marca del sistema</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'smtp' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-white/5 pb-6 gap-6">
                                    <div className="flex-1 space-y-2">
                                        <h3 className="text-2xl font-black text-white">Configuración de Correo (SMTP)</h3>
                                        <p className="text-xs text-white/30 italic">Establezca los parámetros de su servidor de correo institucional.</p>
                                    </div>
                                    <div className="flex items-end gap-3 w-full md:w-auto overflow-hidden">
                                        <div className="flex-1 md:w-64 space-y-1">
                                            <label className="text-[9px] font-black uppercase text-primary-light">Email de Prueba</label>
                                            <input 
                                                type="email" 
                                                placeholder="tu@email.com" 
                                                value={testEmail} 
                                                onChange={e => setTestEmail(e.target.value)} 
                                                className="input-field bg-slate-800/80 text-xs py-2 h-10 border-primary/20 focus:border-primary" 
                                            />
                                        </div>
                                        <button 
                                          onClick={handleTestSMTP}
                                          disabled={testSmtpLoading}
                                          className="bg-primary/20 hover:bg-primary px-6 h-10 rounded-xl text-[10px] font-black flex items-center gap-2 transition-all"
                                        >
                                            {testSmtpLoading ? 'ENVIANDO...' : (
                                                <>
                                                    <Send size={14} /> PROBAR
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Servidor SMTP</label>
                                        <input type="text" placeholder="smtp.ejemplo.com" value={settings.smtp_host || ''} onChange={e => handleChange('smtp_host', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Seguridad (Cifrado)</label>
                                        <select value={settings.smtp_encryption || 'tls'} onChange={e => handleChange('smtp_encryption', e.target.value)} className="input-field bg-slate-800/80">
                                            <option value="none">Ninguno (Inseguro)</option>
                                            <option value="tls">STARTTLS (Recomendado)</option>
                                            <option value="ssl">SSL / TLS (Implicit)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Puerto</label>
                                        <input type="text" placeholder="587 / 465" value={settings.smtp_port || ''} onChange={e => handleChange('smtp_port', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Usuario / Email</label>
                                        <input type="text" value={settings.smtp_user || ''} onChange={e => handleChange('smtp_user', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Contraseña</label>
                                        <input type="password" value={settings.smtp_pass || ''} onChange={e => handleChange('smtp_pass', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary-light">Nombre Remitente</label>
                                        <input type="text" value={settings.smtp_from_name || ''} onChange={e => handleChange('smtp_from_name', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'twilio' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                                    <h3 className="text-2xl font-black text-white">Integración Twilio (SMS)</h3>
                                    <button 
                                      onClick={handleTestSMS}
                                      disabled={testSmsLoading}
                                      className="bg-secondary/20 hover:bg-secondary px-6 py-2 rounded-xl text-[10px] font-black flex items-center gap-2 transition-all"
                                    >
                                        {testSmsLoading ? 'PROBANDO...' : (
                                            <>
                                                <Smartphone size={14} /> PROBAR SMS
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-secondary">Account SID</label>
                                        <input type="text" value={settings.twilio_sid || ''} onChange={e => handleChange('twilio_sid', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-secondary">Auth Token</label>
                                        <input type="password" value={settings.twilio_token || ''} onChange={e => handleChange('twilio_token', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-secondary">Número Remitente (Twilio Number)</label>
                                        <input type="text" value={settings.twilio_number || ''} onChange={e => handleChange('twilio_number', e.target.value)} className="input-field bg-slate-800/80" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'experience' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Experiencia de Usuario (Exploración Visual)</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between p-8 rounded-3xl bg-slate-800/40 border border-white/10 shadow-2xl group transition-all hover:bg-slate-800/60">
                                        <div className="flex items-center gap-6">
                                            <div className="bg-primary/20 p-4 rounded-2xl text-primary-light shadow-xl shadow-primary/20 group-hover:scale-110 transition-transform"><Monitor size={28}/></div>
                                            <div>
                                                <h4 className="text-lg font-black text-white">Modo Mapa Interactivo (Infografía)</h4>
                                                <p className="text-xs text-white/30 max-w-sm mt-1">Sustituye el menú tradicional por una navegación visual basada en el proceso real de tu negocio.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className={`text-[10px] font-black uppercase tracking-widest ${settings.visual_mode === '1' ? 'text-emerald-400' : 'text-white/20'}`}>
                                                {settings.visual_mode === '1' ? 'Activado' : 'Desactivado'}
                                            </span>
                                            <input 
                                                type="checkbox" 
                                                checked={settings.visual_mode === '1'} 
                                                onChange={e => handleChange('visual_mode', e.target.checked ? '1' : '0')} 
                                                className="w-12 h-6 rounded-full appearance-none bg-white/10 checked:bg-emerald-500 relative transition-colors cursor-pointer before:content-[''] before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-1 before:left-1 checked:before:left-7 before:transition-all" 
                                            />
                                        </div>
                                    </div>

                                    {/* Previsualización del Modo Visual */}
                                    {settings.visual_mode === '1' && (
                                        <div className="rounded-3xl overflow-hidden border border-white/5 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                                            <img src="/assets/img/workflow.png" alt="Prev" className="w-full h-auto" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'backup' && (
                            <div className="space-y-8 animate-in fade-in duration-500 text-white">
                                <h3 className="text-2xl font-black text-white border-b border-white/5 pb-4">Gestión de Copias de Seguridad</h3>
                                <div className="glass border-white/10 rounded-3xl p-10 flex flex-col items-center text-center space-y-6">
                                    <div className="bg-primary/20 p-6 rounded-full text-primary-light shadow-2xl shadow-primary/20">
                                        <Database size={48} />
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-black">Respaldo Integral de Datos</h4>
                                        <p className="text-sm text-white/40 max-w-md mx-auto mt-2">
                                            Genera un archivo SQL completo con toda la estructura y datos de PawSpa Pro. Recomendamos realizar este proceso semanalmente.
                                        </p>
                                    </div>
                                    <button 
                                      onClick={handleDownloadBackup}
                                      disabled={descargandoBackup}
                                      className="bg-emerald-500 hover:bg-emerald-600 px-10 py-4 rounded-2xl font-black text-[12px] uppercase tracking-widest shadow-xl shadow-emerald-500/20 transition-all flex items-center gap-3"
                                    >
                                        {descargandoBackup ? 'GENERANDO...' : (
                                            <>
                                                <Download size={20} /> DESCARGAR BACKUP .SQL
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div className="mt-12 p-6 rounded-3xl bg-amber-500/10 border border-amber-500/20 flex gap-4 items-start">
                            <AlertCircle className="text-amber-500 flex-shrink-0" size={20} />
                            <p className="text-xs text-amber-500/80 leading-relaxed font-medium">
                                <strong className="block text-amber-500 mb-1 tracking-tight text-white">Aviso de Seguridad:</strong>
                                Estos cambios afectan globalmente al sistema, facturas y reportes fiscales. Asegúrese de que las tasas de impuestos sean las legales vigentes.
                            </p>
                        </div>
                    </GlassCard>
                </div>
            </div>
        </div>
    );
};

export default Settings;
